import { useEffect, useState } from 'react';
import Button from 'components/Button';
import {
    fetchAlertDirectoryTopics,
    fetchDirectoryTopics,
    saveAlertDirectoryTopics,
} from 'api/directory';
import { newsroomError } from 'api/newsroom';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const sameSet = (a, b) =>
    a.length === b.length && a.every((value) => b.includes(value));

const toggled = (list, value) =>
    list.includes(value)
        ? list.filter((other) => other !== value)
        : [...list, value];

// Admins only. Saved on its own button, apart from the story or alert form: it changes the
// directory pages, not this one.
const DirectoryTopicsPicker = ({ alertId, published }) => {
    const [options, setOptions] = useState(null);
    const [saved, setSaved] = useState({ expertiseIds: [], townSlugs: [] });
    const [topicIds, setTopicIds] = useState([]);
    const [townSlugs, setTownSlugs] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState(null);

    const apply = (current) => {
        setSaved(current);
        setTopicIds(current.expertiseIds);
        setTownSlugs(current.townSlugs || []);
    };

    useEffect(() => {
        let active = true;
        setOptions(null);
        setNotice(null);
        Promise.all([
            fetchDirectoryTopics(),
            fetchAlertDirectoryTopics(alertId),
        ])
            .then(([list, current]) => {
                if (!active) return;
                setOptions({ topics: list.topics, towns: list.towns || [] });
                apply(current);
            })
            .catch((err) => {
                if (active)
                    setNotice({
                        ok: false,
                        text: newsroomError(
                            err,
                            'Could not load the directory topics.'
                        ),
                    });
            });
        return () => {
            active = false;
        };
    }, [alertId]);

    const save = async () => {
        setSaving(true);
        setNotice(null);
        try {
            apply(await saveAlertDirectoryTopics(alertId, topicIds, townSlugs));
            setNotice({
                ok: true,
                text: published
                    ? 'Saved. The directory pages update within a minute.'
                    : 'Saved. The story goes on these pages when it is published.',
            });
        } catch (err) {
            setNotice({
                ok: false,
                text: newsroomError(err, 'Could not save the topics.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const topics = options?.topics || [];
    const withoutPage = topics.filter(
        (topic) => !topic.hasPage && !topicIds.includes(topic.id)
    );
    const shownTopics = topics.filter(
        (topic) => showAll || topic.hasPage || topicIds.includes(topic.id)
    );
    // A town ticked earlier stays listed after its page has gone, so it can be unticked.
    const towns = [
        ...(options?.towns || []),
        ...(saved.townSlugs || [])
            .filter(
                (slug) => !options?.towns?.some((town) => town.slug === slug)
            )
            .map((slug) => ({ slug, name: slug, gone: true })),
    ];
    const unchanged =
        sameSet(topicIds, saved.expertiseIds) &&
        sameSet(townSlugs, saved.townSlugs || []);

    return (
        <div className={styles.topics} aria-label="Directory topics">
            <strong>Directory topics</strong>
            <p className={styles.hint}>
                Tick every mortgage-brokers page this story belongs on. The
                experts quoted in it, and coverage logged from this alert, show
                on those pages too.
            </p>
            {!options && !notice && <p className={styles.hint}>Loading…</p>}
            {options && (
                <>
                    <span className={styles.topicGroup}>Topics</span>
                    <ul className={styles.topicList}>
                        {shownTopics.map((topic) => (
                            <li key={topic.id}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={topicIds.includes(topic.id)}
                                        disabled={saving}
                                        onChange={() =>
                                            setTopicIds((current) =>
                                                toggled(current, topic.id)
                                            )
                                        }
                                    />
                                    <span>{topic.name}</span>
                                    {!topic.hasPage && (
                                        <span className={styles.topicNoPage}>
                                            no page yet
                                        </span>
                                    )}
                                </label>
                            </li>
                        ))}
                    </ul>
                    {withoutPage.length > 0 && (
                        <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => setShowAll((value) => !value)}
                        >
                            {showAll
                                ? 'Hide topics without a page'
                                : `Show ${withoutPage.length} topics without a page`}
                        </button>
                    )}
                    {towns.length > 0 && (
                        <>
                            <span className={styles.topicGroup}>Towns</span>
                            <ul className={styles.topicList}>
                                {towns.map((town) => (
                                    <li key={town.slug}>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={townSlugs.includes(
                                                    town.slug
                                                )}
                                                disabled={saving}
                                                onChange={() =>
                                                    setTownSlugs((current) =>
                                                        toggled(
                                                            current,
                                                            town.slug
                                                        )
                                                    )
                                                }
                                            />
                                            <span>{town.name}</span>
                                            {town.gone && (
                                                <span
                                                    className={
                                                        styles.topicNoPage
                                                    }
                                                >
                                                    no page now
                                                </span>
                                            )}
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    <div className={styles.topicActions}>
                        <Button
                            title={'Save topics'}
                            theme={'secondary'}
                            loading={saving}
                            disabled={unchanged}
                            onClick={save}
                        />
                    </div>
                </>
            )}
            {notice && (
                <p
                    className={notice.ok ? styles.ok : styles.error}
                    role={notice.ok ? 'status' : 'alert'}
                >
                    {notice.text}
                </p>
            )}
        </div>
    );
};

export default DirectoryTopicsPicker;
